import React from 'react';
import { Hexagram, trigramNameZh } from '../core/meihua';

export const SlimHexagramView: React.FC<{ hex: Hexagram; highlightMoving?: boolean }> = ({ hex, highlightMoving }) => {
  const buildLines = (hexagram: Hexagram): ('yang' | 'yin')[] => {
    const getTrigramLines = (t: Hexagram['upper']): ('yang' | 'yin')[] => {
      switch (t) {
        case 1: return ['yang', 'yang', 'yang'];
        case 2: return ['yang', 'yang', 'yin'];
        case 3: return ['yang', 'yin', 'yang'];
        case 4: return ['yang', 'yin', 'yin'];
        case 5: return ['yin', 'yang', 'yang'];
        case 6: return ['yin', 'yang', 'yin'];
        case 7: return ['yin', 'yin', 'yang'];
        case 8: return ['yin', 'yin', 'yin'];
        default: return ['yang', 'yang', 'yang'];
      }
    };
    return [...getTrigramLines(hexagram.lower), ...getTrigramLines(hexagram.upper)];
  };

  const lines = buildLines(hex);
  const moving = hex.movingLine ?? -1;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '80px',
        margin: '0 auto',
        padding: '6px 4px',
        borderRadius: '8px',
        backgroundColor: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        boxShadow: 'none'
      }}
    >
      {[5, 4, 3, 2, 1, 0].map((idx) => {
        const yao = lines[idx];
        const lineNo = idx + 1;
        const highlight = !!highlightMoving && moving === lineNo;
        const baseColor = highlight ? '#e05244' : 'var(--text-primary)';
        if (yao === 'yang') {
          return (
            <div
              key={idx}
              className="hex-line"
              style={{
                width: '100%',
                height: '4px',
                borderRadius: '2px',
                backgroundColor: baseColor,
                boxShadow: highlight ? '0 0 3px rgba(255, 59, 48, 0.5)' : 'none'
              }}
            />
          );
        }
        return (
          <div key={idx} style={{ display: 'flex', width: '100%', justifyContent: 'space-between', height: '4px' }}>
            <div
              className="hex-line"
              style={{
                width: '40%',
                height: '100%',
                borderRadius: '2px',
                backgroundColor: baseColor,
                boxShadow: highlight ? '0 0 3px rgba(255, 59, 48, 0.5)' : 'none'
              }}
            />
            <div style={{ width: '20%' }} />
            <div
              className="hex-line"
              style={{
                width: '40%',
                height: '100%',
                borderRadius: '2px',
                backgroundColor: baseColor,
                boxShadow: highlight ? '0 0 3px rgba(255, 59, 48, 0.5)' : 'none'
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export interface HexagramSummaryProps {
  label: string;
  hexagram: Hexagram;
  relation?: string;
  showStructure?: boolean;
}

const getRelationColor = (relation: string) => {
  switch (relation) {
    case '用生体': return '#4caf6d'; // Green (Good)
    case '比和': return '#4a90d9'; // Blue (Harmony)
    case '体克用': return '#c99a5b'; // Orange (Control)
    case '体生用': return '#7d7769'; // Gray (Drain)
    case '用克体': return '#e05244'; // Red (Clash/Bad)
    default: return 'var(--primary-color)';
  }
};

export const HexagramSummary: React.FC<HexagramSummaryProps> = ({ label, hexagram, relation, showStructure = true }) => {
  const structure = `上${trigramNameZh(hexagram.upper)}下${trigramNameZh(hexagram.lower)}`;
  const relationColor = relation ? getRelationColor(relation) : 'var(--primary-color)';
  
  return (
    <div style={{ flex: '1 1 auto', minWidth: '0', textAlign: 'center', padding: '4px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>{label}</div>
      <SlimHexagramView hex={hexagram} highlightMoving={label === '变卦'} />
      <div style={{ marginTop: '6px' }}>
        {showStructure && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.2 }}>{structure}</div>
        )}
        {relation && (
          <div style={{ fontSize: 11, fontWeight: 600, color: relationColor, marginTop: '2px' }}>{relation}</div>
        )}
      </div>
    </div>
  );
};
